import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, addDoc, updateDoc,
  collection, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@services/firebase';
import {
  ArrowLeft, Upload, X, Image, Video, Save, Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORIES = [
  'Technology', 'AI', 'Data Science', 'Business', 'Tutorial', 'News', 'General',
];

export default function AdminPostEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const fileInputRef = useRef(null);

  const [loading, setLoading]             = useState(isEdit);
  const [saving, setSaving]               = useState(false);
  const [uploading, setUploading]         = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mediaType, setMediaType]         = useState(null); // 'image' | 'video'
  const [previewUrl, setPreviewUrl]       = useState('');

  const [form, setForm] = useState({
    title:     '',
    excerpt:   '',
    content:   '',
    category:  'General',
    published: false,
    imageUrl:  '',
    videoUrl:  '',
  });

  /* ── Load post for editing ─────────────────────────────────────── */
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'blog_posts', id));
        if (!snap.exists()) {
          toast.error('Post not found');
          navigate('/admin/posts');
          return;
        }
        const data = snap.data();
        setForm({
          title:     data.title    || '',
          excerpt:   data.excerpt  || '',
          content:   data.content  || data.body || '',
          category:  data.category || data.platform || 'General',
          published: data.published !== false,
          imageUrl:  data.imageUrl || data.image_url || data.coverImage || '',
          videoUrl:  data.videoUrl || '',
        });
        if (data.videoUrl) {
          setMediaType('video');
          setPreviewUrl(data.videoUrl);
        } else if (data.imageUrl || data.image_url || data.coverImage) {
          setMediaType('image');
          setPreviewUrl(data.imageUrl || data.image_url || data.coverImage);
        }
      } catch {
        toast.error('Failed to load post');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  /* ── Form helpers ──────────────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const togglePublished = () =>
    setForm(prev => ({ ...prev, published: !prev.published }));

  /* ── File upload to Firebase Storage ─────────────────────────── */
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    if (!isVideo && !isImage) {
      toast.error('Only images and videos are supported');
      return;
    }

    // Use a temp folder if creating a new post (no id yet)
    const folder = id || `draft_${Date.now()}`;
    const storageRef = ref(storage, `blog-media/${folder}/${file.name}`);

    setUploading(true);
    setUploadProgress(0);

    const task = uploadBytesResumable(storageRef, file);

    task.on(
      'state_changed',
      (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => {
        console.error('Storage upload error:', err);
        toast.error(`Upload failed: ${err.code ?? err.message}`);
        setUploading(false);
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        if (isVideo) {
          setForm(prev => ({ ...prev, videoUrl: url, imageUrl: '' }));
          setMediaType('video');
        } else {
          setForm(prev => ({ ...prev, imageUrl: url, videoUrl: '' }));
          setMediaType('image');
        }
        setPreviewUrl(url);
        setUploading(false);
        toast.success('Media uploaded!');
      }
    );
  };

  const clearMedia = () => {
    setForm(prev => ({ ...prev, imageUrl: '', videoUrl: '' }));
    setMediaType(null);
    setPreviewUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /* ── URL-paste handler ────────────────────────────────────────── */
  const handleUrlChange = (e) => {
    const url = e.target.value.trim();
    const isVid = /\.(mp4|mov|webm|ogv)(\?.*)?$/i.test(url);
    setForm(prev => ({
      ...prev,
      imageUrl: isVid ? '' : url,
      videoUrl: isVid ? url : '',
    }));
    setMediaType(url ? (isVid ? 'video' : 'image') : null);
    setPreviewUrl(url);
  };

  /* ── Save ─────────────────────────────────────────────────────── */
  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title:     form.title.trim(),
        excerpt:   form.excerpt.trim(),
        content:   form.content.trim(),
        category:  form.category,
        published: form.published,
        imageUrl:  form.imageUrl,
        videoUrl:  form.videoUrl,
        updatedAt: serverTimestamp(),
      };

      if (isEdit) {
        await updateDoc(doc(db, 'blog_posts', id), payload);
        toast.success('Post updated!');
      } else {
        payload.createdAt = serverTimestamp();
        await addDoc(collection(db, 'blog_posts'), payload);
        toast.success('Post created!');
      }
      navigate('/admin/posts');
    } catch {
      toast.error('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  /* ── Loading state ─────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-gray-400">
        <Loader2 size={24} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/posts')}
          className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            {isEdit ? 'Edit Post' : 'New Post'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isEdit ? 'Update your blog post' : 'Create a new blog post'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Left / Main content (2/3) ────────────────────────── */}
        <div className="lg:col-span-2 space-y-5">

          {/* Title */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Enter post title…"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Excerpt */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Excerpt
              <span className="ml-1 text-gray-400 font-normal text-xs">
                (shown in post listings)
              </span>
            </label>
            <textarea
              name="excerpt"
              value={form.excerpt}
              onChange={handleChange}
              rows={2}
              placeholder="Short description…"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Content
              <span className="ml-1 text-gray-400 font-normal text-xs">
                (Markdown supported)
              </span>
            </label>
            <textarea
              name="content"
              value={form.content}
              onChange={handleChange}
              rows={22}
              placeholder="Write your post content here…&#10;&#10;# Heading&#10;**Bold**, *italic*, [link](url)"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-mono resize-y leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* ── Right / Sidebar (1/3) ────────────────────────────── */}
        <div className="space-y-5">

          {/* Publish settings */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Publish Settings</h3>

            {/* Toggle */}
            <label className="flex items-center gap-3 cursor-pointer" onClick={togglePublished}>
              <div
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  form.published ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    form.published ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </div>
              <span className="text-sm text-gray-600">
                {form.published ? 'Published' : 'Draft'}
              </span>
            </label>

            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={saving || uploading}
              className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {saving
                ? <><Loader2 size={14} className="animate-spin" /> Saving…</>
                : <><Save size={14} /> {isEdit ? 'Update Post' : 'Create Post'}</>
              }
            </button>
          </div>

          {/* Category */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Cover media */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Cover Media</h3>

            {/* Preview */}
            {previewUrl ? (
              <div className="relative mb-3">
                {mediaType === 'video' ? (
                  <video
                    src={previewUrl}
                    className="w-full rounded-xl max-h-48 object-cover"
                    controls
                  />
                ) : (
                  <img
                    src={previewUrl}
                    alt="Cover preview"
                    className="w-full rounded-xl object-cover max-h-48"
                  />
                )}
                <button
                  onClick={clearMedia}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              /* Drop zone */
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors mb-3"
              >
                <div className="flex gap-3 text-gray-300">
                  <Image size={22} />
                  <Video size={22} />
                </div>
                <p className="text-xs text-gray-500 text-center leading-relaxed">
                  Click to upload image or video
                </p>
                <p className="text-xs text-gray-400">JPG · PNG · GIF · MP4 · MOV</p>
              </div>
            )}

            {/* Upload progress */}
            {uploading && (
              <div className="mb-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Uploading to Firebase Storage…</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-200"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Browse button */}
            {!uploading && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 rounded-xl text-xs text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <Upload size={13} />
                {previewUrl ? 'Replace File' : 'Browse File'}
              </button>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/mp4,video/quicktime,video/webm"
              className="hidden"
              onChange={handleFileUpload}
            />

            {/* Paste URL fallback */}
            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">Or paste URL</label>
              <input
                value={form.imageUrl || form.videoUrl}
                onChange={handleUrlChange}
                placeholder="https://… or storage URL"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Storage bucket info */}
            <p className="mt-3 text-xs text-gray-400 leading-relaxed">
              Files are stored in{' '}
              <span className="font-mono text-gray-500 break-all">
                gs://datalogichub.firebasestorage.app/blog-media/
              </span>
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}
